import { useGrowl } from "PFApp/use_growl";
import { ActivitiesCollection } from "PFCore/hooks/queries/activities/query_keys";
import { useActivitiesInvalidate } from "PFCore/hooks/queries/activities/use_activities_invalidate";
import { RefreshMatchingActivityResponse } from "PFCore/services/matching_activities/refresh_matching_activity";
import { Id } from "PFTypes";
import { useTranslation } from "react-i18next";

import { useMarketplaceContext } from "../../contexts/marketplace_context";
import { useMatchingActivityScoresReplace } from "./use_matching_activity_scores_replace";

type UseRefreshMatchReturn = {
  onRefreshSuccess: (response: RefreshMatchingActivityResponse) => void;
};

export const useRefreshMatch = (roleId: Id): UseRefreshMatchReturn => {
  const growl = useGrowl();
  const { t } = useTranslation("translation", { keyPrefix: "refreshMatch" });
  const { invalidateByCollection } = useActivitiesInvalidate();
  const { selectedRoleId, setSelectedRole } = useMarketplaceContext();
  const { replaceMatchingActivityScoresInCache } = useMatchingActivityScoresReplace();

  const onRefreshSuccess = (response: RefreshMatchingActivityResponse) => {
    const refreshedRole = response.entries.find(({ id }) => id === roleId);
    if (refreshedRole) {
      // Replace the role with the updated scores
      replaceMatchingActivityScoresInCache(roleId, refreshedRole.scores);
    } else {
      // User is no longer matching this role
      invalidateByCollection(ActivitiesCollection.MatchingActivities);
    }

    if (selectedRoleId !== null && refreshedRole?.activity?.id === selectedRoleId) {
      setSelectedRole(selectedRoleId, refreshedRole.scores);
    }
    invalidateByCollection(ActivitiesCollection.MarketplaceActivity, roleId);
    invalidateByCollection(ActivitiesCollection.Interested);
    invalidateByCollection(ActivitiesCollection.Saved);
    invalidateByCollection(ActivitiesCollection.Shared);
    invalidateByCollection(ActivitiesCollection.Statistics);

    growl({
      message: t("updated"),
      kind: "success"
    });
  };

  return {
    onRefreshSuccess
  };
};
